import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  X,
  UserPlus,
  Trophy,
  AtSign,
  Heart,
  MessageCircle,
  Bell,
  Church,
  BookOpen,
  Cross,
  HandHeart,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { getAssetUrl } from "../../services/helper";

// Icon Map
const iconMap = {
  follow: { icon: UserPlus, bg: "bg-emerald-50", color: "text-emerald-600" },
  new_challenge: { icon: Trophy, bg: "bg-purple-50", color: "text-purple-600" },
  trophy: { icon: Trophy, bg: "bg-purple-50", color: "text-purple-600" },
  mention: { icon: AtSign, bg: "bg-amber-50", color: "text-amber-600" },
  like: { icon: Heart, bg: "bg-rose-50", color: "text-rose-500" },
  comment: { icon: MessageCircle, bg: "bg-blue-50", color: "text-blue-600" },
  like_post: { icon: Heart, bg: "bg-rose-50", color: "text-rose-500" },
  comment_post: { icon: MessageCircle, bg: "bg-blue-50", color: "text-blue-600" },
  system: { icon: Bell, bg: "bg-gray-100", color: "text-gray-500" },
  prayer: { icon: HandHeart, bg: "bg-indigo-50", color: "text-indigo-600" },
  church: { icon: Church, bg: "bg-sky-50", color: "text-sky-600" },
  bible: { icon: BookOpen, bg: "bg-amber-50", color: "text-amber-700" },
  cross: { icon: Cross, bg: "bg-red-50", color: "text-red-600" },
};

const getNotificationIcon = (type) => {
  return iconMap[type] || { icon: Bell, bg: "bg-gray-100", color: "text-gray-500" };
};

const NotificationsModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();

  // Fetch Notifications
  const { data, isLoading, isError } = useQuery({
    queryKey: ["notifications-modal"],
    queryFn: () => api.fetchNotifications(1, 3), // Adjust params if needed (page, limit)
    enabled: isOpen, // Only fetch when modal is open
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const notifications = data?.data || [];

  // Mark as Read Mutation
  const { mutate: markAsRead } = useMutation({
    mutationFn: (id) => api.markUserNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-modal"] });
      queryClient.invalidateQueries({ queryKey: ["user-notifications"] }); // If you have main notifications page
    },
  });

  const unreadCount = notifications.filter((n) => n.is_read === 0).length;

  const handleMarkAsRead = (id) => {
    markAsRead(id);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[2px] flex items-center justify-center sm:block"
      onClick={onClose}
    >
      <div
        className="sm:absolute sm:right-5 sm:top-[80px] relative mx-auto sm:mx-0 w-[420px] max-w-[calc(100%-2rem)] max-h-[80vh] sm:max-h-[60vh] bg-[#f8fafc] rounded-[14px] shadow-[0_20px_60px_rgba(15,29,46,0.18)] overflow-hidden border border-[#E8EDF3] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 bg-[#f8fafc] sticky top-0 z-20 border-b border-[#E8EDF3]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-[17px] font-semibold text-[#0F1D2E]">Notifications</h2>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-[#1F3A60] text-white rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>

            <button onClick={onClose} className="hover:bg-[#F7F9FC] p-2 rounded-full transition-all">
              <X size={20} className="text-[#526071]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto notifications-scroll">
          <style>{`
            .notifications-scroll::-webkit-scrollbar { display: none; }
          `}</style>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-60">
              <Loader2 size={28} className="animate-spin text-[#1F3A60]" />
              <p className="text-sm text-[#7B8794] mt-3">Loading notifications...</p>
            </div>
          ) : isError ? (
            <div className="p-8 text-center text-[#7B8794]">
              Failed to load notifications
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-[#7B8794]">
              No notifications yet
            </div>
          ) : (
            notifications.map((notif) => {
              const { icon: Icon, bg, color } = getNotificationIcon(notif.type);
              const isUnread = notif.is_read === 0;

              return (
                <div
                  key={notif.id}
                  onClick={() => isUnread && handleMarkAsRead(notif.id)}
                  className={`relative flex items-start gap-4 px-5 py-4 transition-all hover:bg-[#F8FAFC] cursor-pointer ${isUnread ? "bg-[#1F3A60]/[0.03]" : ""
                    }`}
                >
                  {/* Unread Indicator */}
                  {isUnread && (
                    <div className="absolute left-3 top-6 w-2 h-2 rounded-full bg-[#1F3A60]" />
                  )}

                  {/* Icon Circle */}
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${bg}`}>
                    <Icon className={`w-5 h-5 ${color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="text-[13.5px] leading-[21px] text-[#334155]">
                      {notif.actor_name ? (
                        <>
                          <span className="font-semibold text-[#0F1D2E]">
                            {notif.actor_name}
                          </span>
                          <span className="text-[#526071]"> </span>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: notif.message.startsWith(notif.actor_name)
                                ? notif.message.replace(notif.actor_name, "")
                                : notif.message
                            }}
                          />
                        </>
                      ) : (
                        <span
                          dangerouslySetInnerHTML={{ __html: notif.message }}
                        />
                      )}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <p className="text-[11px] font-medium text-[#94A3B8]">
                        {new Date(notif.created_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Avatar if available */}

                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="bg-[#FAFBFC] px-5 py-4 border-t border-[#E8EDF3]">
          <Link to="/notifications" onClick={onClose}>
            <button className="w-full h-[46px] rounded-2xl bg-[#1F3A60] text-white text-[13px] font-semibold hover:opacity-95 transition-all">
              View All Notifications
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotificationsModal;